import { Children, Fragment } from "react";
import Btn from "../button/Btn";
import IconBtn from "../button/IconBtn";
import Chip from "../chips/Chip";
import CustomDropdown from "../dropdowns/CustomDropdown";
import CustomTooltip from "../CustomTooltip";
import { DropDownIcon } from "../../icons/inputIcons";
import { RoundedUser } from "../../icons/btnIcons";

// One header for every details page (customer, lead, quotation, follow-up, product):
//   ‹  [icon]  Title
//              chips · meta items · owner            [ toolbar ]
//   [ fact | fact | fact … ]

export const CHIP_COLORS = {
  green: "#15803d",
  orange: "#c2410c",
  amber: "#b45309",
  blue: "#213b73",
  teal: "#0e7490",
  grey: "#64748b",
};

export const initialsOf = (name = "") =>
  name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((w) => w[0])
    .join("")
    .toUpperCase() || "?";

// Initials in a circle (owner, contacts)
export const Avatar = ({ name, size = 28, className = "bg-primary-10 primary-text" }) => (
  <i
    className={`flex-center ratio-1 rounded-circle flex-shrink-0 fst-normal fw-medium ${className}`}
    style={{ width: size, fontSize: Math.round(size * 0.36) }}
  >
    {initialsOf(name || "")}
  </i>
);

// One cell of the facts strip under the title; hidden when there is no value
export const Fact = ({ label, icon, children }) =>
  children ? (
    <div className="cd-fact d-flex align-items-center gap-5 min-w-0">
      {icon && (
        <i className="cd-fact-icon flex-center rounded-circle bg-primary-10 primary-text flex-shrink-0">
          {icon}
        </i>
      )}
      <div className="flex-column gap-1 min-w-0">
        <span className="fs-xs text-secondary">{label}</span>
        {/* full value on hover when it is cut with "…" */}
        <span
          className="fs-sm text-primary text-truncate"
          title={typeof children === "string" ? children : undefined}
        >
          {children}
        </span>
      </div>
    </div>
  ) : null;

// One white strip with thin dividers instead of a card per fact
export const Facts = ({ children }) => <div className="cd-facts">{children}</div>;

// Icon of the page type (customer, lead, quotation…) next to the title
export const TitleIcon = ({ icon }) => (
  <i
    className="flex-center ratio-1 rounded-4 bg-primary-10 primary-text fst-normal flex-shrink-0"
    style={{ width: 40 }}
    aria-hidden="true"
  >
    {icon}
  </i>
);

// Small chip used in the grey line under the title
export const MetaChip = ({ color, children, className = "", ...rest }) => (
  <Chip
    color={color}
    className={`rounded-pill py-1 px-4 fs-xs fw-normal text-nowrap ${className}`}
    {...rest}
  >
    {children}
  </Chip>
);

// Grey line under the title: chips first, then items; each item's icon separates it
export const MetaLine = ({ chips = [], items = [] }) => {
  const shownChips = chips.filter(Boolean);
  const shown = items.filter(Boolean);
  if (!shown.length && !shownChips.length) return null;
  return (
    <div className="d-flex flex-wrap align-items-center column-gap-7 row-gap-2 fs-xs text-secondary">
      {shownChips.length > 0 && (
        <span className="d-inline-flex flex-wrap align-items-center gap-3">
          {shownChips.map((chip, i) => (
            <span key={i} className="d-inline-flex">
              {chip}
            </span>
          ))}
        </span>
      )}
      {shown.map((item, i) => (
        <span key={i} className="d-inline-flex align-items-center">
          {item}
        </span>
      ))}
    </div>
  );
};

// One item in the grey line: small muted icon + text
export const MetaItem = ({ icon, children }) => (
  <span className="d-inline-flex align-items-center gap-2 min-w-0">
    {icon && <span className="d-inline-flex flex-shrink-0 opacity-75">{icon}</span>}
    {children}
  </span>
);

// "Assigned to Akash Sonkar ▾" as plain text; click to change when allowed
export const OwnerPicker = ({ id, name, prefix = "Assigned to", options, onSelect, disabled }) => {
  const text = name ? (
    <span className="d-inline-flex align-items-center gap-2">
      <Avatar name={name} size={18} />
      {prefix} <span className="text-primary">{name}</span>
    </span>
  ) : (
    <MetaItem icon={<RoundedUser size={13} />}>Not assigned</MetaItem>
  );
  if (disabled || !options?.length) return <span>{text}</span>;
  return (
    <CustomDropdown
      as={Btn}
      id={id}
      toggleProps={{
        variant: "outline-none",
        className: "border-0 p-0 fs-xs text-secondary gap-2 enable-after",
        areaTitle: "Change who is assigned",
        children: text,
      }}
      options={options}
      onSelect={onSelect}
      menuProps={{ className: "p-4" }}
      listProps={{ className: "overflow-auto custom-scroll", style: { maxHeight: 280 } }}
      itemProps={{ className: "px-3 py-2 d-flex gap-4 align-items-center rounded-2", as: "button" }}
    />
  );
};

// One toolbar button with a tooltip. With `label` it shows text (e.g. "● Active"), otherwise icon only.
// tone: "success" | "danger" colours it; end: tooltip lines up right (last button)
export const HeaderAction = ({ tip, icon, label, onClick, href, disabled, tone, end, ...rest }) => {
  const color = tone === "danger" ? "text-danger" : tone === "success" ? "text-success" : "";
  const button = label ? (
    <Btn
      variant="outline-none"
      className={`cd-tool-btn gap-3 ${color}`}
      onClick={onClick}
      href={href}
      disabled={disabled}
      aria-label={typeof tip === "string" ? tip : undefined}
      {...rest}
    >
      {icon}
      {label}
    </Btn>
  ) : (
    <IconBtn
      variant="outline-none"
      className={`cd-tool-btn ${color}`}
      onClick={onClick}
      href={href}
      disabled={disabled}
      icon={icon}
      aria-label={typeof tip === "string" ? tip : undefined}
      {...rest}
    />
  );
  if (!tip) return button;
  return (
    <CustomTooltip
      content={tip}
      position="bottom"
      className={`text-nowrap ${end ? "cd-tip-end" : ""}`}
    >
      {/* span so the tooltip still shows on a disabled button */}
      <span className="d-inline-flex">{button}</span>
    </CustomTooltip>
  );
};

// Dropdown in the toolbar (Add new, Download…), same look as HeaderAction
export const HeaderMenu = ({ id, tip, icon, options, onSelect, disabled }) => (
  <CustomDropdown
    as={IconBtn}
    id={id}
    overlayText={tip}
    overlayPosition="bottom"
    toggleProps={{
      variant: "outline-none",
      className: "cd-tool-btn",
      icon,
      disabled,
      "aria-label": tip,
    }}
    options={options}
    onSelect={onSelect}
    menuProps={{ className: "p-4", align: "end" }}
    itemProps={{ className: "px-3 py-2 d-flex gap-4 align-items-center rounded-2", as: "button" }}
  />
);

/**
 * Header shared by all details pages.
 * - icon: page-type icon (building, bag, quotation…)
 * - title, capitalize: record name
 * - chips / meta: grey line under the title (empty ones are skipped)
 * - actions: toolbar buttons (HeaderAction / HeaderMenu); falsy ones are skipped, dividers added between
 * - children: <Fact /> cells for the strip under the title (empty ones hide themselves)
 */
const DetailsHeader = ({
  icon,
  title,
  capitalize = false,
  backLabel = "Back",
  onBack,
  chips = [],
  meta = [],
  actions = [],
  children,
}) => {
  const tools = actions.filter(Boolean);
  const hasFacts = Children.toArray(children).length > 0;

  return (
    <div className="cd-header bg-light-subtle border-bottom px-12 py-7 flex-column gap-7">
      <div className="d-flex flex-wrap align-items-center gap-6">
        <IconBtn
          icon={<DropDownIcon size={22} />}
          variant="link"
          className="p-0 rotate-90 primary-text flex-shrink-0"
          onClick={onBack}
          aria-label={backLabel}
        />
        {icon && <TitleIcon icon={icon} />}

        <div className="flex-column gap-3 min-w-0">
          <h5
            className={`mb-0 fw-medium primary-text lh-sm ${capitalize ? "text-capitalize" : ""}`}
          >
            {title}
          </h5>
          <MetaLine chips={chips} items={meta} />
        </div>

        {tools.length > 0 && (
          <div className="ms-auto cd-tool-group">
            {tools.map((tool, i) => (
              <Fragment key={i}>
                {i > 0 && <span className="vr my-3 mx-1 text-secondary" />}
                {tool}
              </Fragment>
            ))}
          </div>
        )}
      </div>

      {hasFacts && <Facts>{children}</Facts>}
    </div>
  );
};

export default DetailsHeader;
